import ApprovalsBell from '../../features/approvals/ApprovalsBell';
import React from 'react';
import type { CSSProperties } from 'react';
import type { Tokens } from '../../theme/tokens';
import type { AuthUser } from '../../types';
import { useTheme } from '../../theme/ThemeContext';
import { getTokens } from '../../theme/tokens';
import { useChurchConfig } from '../../hooks';
import { APP_TITLE } from '../../utils/constants';
import churchLogo from '../../church.png';
import { Icon } from '../ui';

/** The bar's rendered height: 15px padding x 2 + 38px logo + 1px border. Sticky things sit below it. */
export const NAVBAR_HEIGHT = 69;

const navStyles = (t: Tokens): Record<string, CSSProperties> => ({
  navbar: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '15px 30px',
    backgroundColor: t.isDarkMode ? '#1E1E1E' : 'rgba(255, 255, 255, 0.9)',
    backdropFilter: t.isDarkMode ? 'none' : 'blur(10px)',
    borderBottom: t.isDarkMode ? '1px solid #333' : '1px solid rgba(255, 255, 255, 0.3)',
    boxShadow: t.shadowRaised,
    width: '100%', boxSizing: 'border-box',
    position: 'sticky', top: 0, zIndex: t.zIndex.navbar,
    transition: 'background-color 0.3s ease, border-color 0.3s ease'
  },
  navLeft: { display: 'flex', alignItems: 'center', gap: '14px', minWidth: 0 },
  /** The menu button for the sidebar layout; CSS shows it only on a narrow screen. */
  menuBtn: {
    alignItems: 'center', justifyContent: 'center', width: '40px', height: '40px', flexShrink: 0,
    background: 'none', border: `1px solid ${t.isDarkMode ? '#444' : '#D4CCC4'}`,
    borderRadius: t.radius.md, cursor: 'pointer', fontSize: '20px',
    color: t.isDarkMode ? t.primaryOnDark : t.logo
  },
  navRight: { display: 'flex', alignItems: 'center', gap: '15px' },
  /** Clicking the mark goes home, so it is a button, not a heading. */
  brand: {
    display: 'flex', alignItems: 'center', gap: '12px',
    background: 'none', border: 'none', padding: 0, minWidth: 0,
    color: t.isDarkMode ? t.primaryOnDark : t.logo,
    textAlign: 'left'
  },
  logoImage: {
    height: '38px', width: '38px', objectFit: 'contain',
    borderRadius: t.radius.md, flexShrink: 0,
    // A pale plate keeps a dark or transparent logo readable on either theme.
    backgroundColor: t.isDarkMode ? 'rgba(255,255,255,0.9)' : 'transparent',
    padding: t.isDarkMode ? '3px' : 0, boxSizing: 'border-box'
  },
  logoText: {
    margin: 0, fontSize: '19px', fontWeight: t.fontWeight.black, letterSpacing: '0.5px',
    textTransform: 'uppercase', color: 'inherit',
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
  },
  /** Divides the brand from the back action so they do not read as one block. */
  divider: {
    width: '1px', height: '26px', flexShrink: 0,
    backgroundColor: t.isDarkMode ? '#3A3A3A' : '#E3E3E3'
  },
  backBtn: {
    display: 'flex', alignItems: 'center', gap: '6px',
    backgroundColor: t.bgAccent,
    border: `1px solid ${t.isDarkMode ? 'rgba(212, 182, 228, 0.25)' : '#D1C4E9'}`,
    color: t.isDarkMode ? t.primaryOnDark : t.logo,
    padding: '7px 16px', borderRadius: t.radius.pill, cursor: 'pointer',
    fontWeight: t.fontWeight.bold, fontSize: t.fontSize.sm,
    whiteSpace: 'nowrap', transition: 'all 0.2s ease'
  },
  pillBtn: {
    padding: '6px 12px', borderRadius: t.radius.pill, cursor: 'pointer',
    fontWeight: t.fontWeight.bold, fontSize: t.fontSize.sm,
    display: 'flex', alignItems: 'center', gap: '5px', transition: 'all 0.3s ease'
  },
  settingsBtn: {
    backgroundColor: t.isDarkMode ? '#2A2A2A' : '#ECEFF1',
    border: `1px solid ${t.isDarkMode ? '#444' : '#CFD8DC'}`,
    color: t.isDarkMode ? '#FFF' : '#37474F'
  },
  themeBtn: {
    backgroundColor: t.isDarkMode ? '#333333' : '#F0F0F0',
    border: `1px solid ${t.isDarkMode ? '#555' : '#D4CCC4'}`,
    color: t.textMain
  },
  logoutBtn: {
    backgroundColor: t.danger, color: '#FFF', border: `1px solid ${t.dangerDark}`,
    padding: '6px 16px', boxShadow: '0 2px 5px rgba(211,47,47,0.3)'
  },
  userProfile: {
    color: t.isDarkMode ? t.primaryOnDark : t.logo,
    fontWeight: t.fontWeight.bold, fontSize: t.fontSize.sm, padding: '8px 16px',
    backgroundColor: t.bgAccent,
    border: `1px solid ${t.isDarkMode ? 'rgba(212, 182, 228, 0.2)' : '#D1C4E9'}`,
    borderRadius: t.radius.pill, transition: 'all 0.3s ease'
  }
});

export interface NavbarProps {
  user: AuthUser | null;
  isAdmin?: boolean;
  /** On the dashboard, where the brand mark is plain text rather than a home link. */
  atHome?: boolean;
  /** The "Back to Dashboard" button (the tile layout, which has no menu). */
  showBack?: boolean;
  /** The Settings pill (the tile layout; the other layouts list Settings in their menu). */
  showSettings?: boolean;
  /** The menu button that opens the sidebar drawer on a narrow screen. */
  showMenuButton?: boolean;
  onToggleMenu?: () => void;
  onHome?: () => void;
  onOpenSettings?: () => void;
  onLogout?: () => void;
}

const Navbar = ({
  user, isAdmin, atHome = false, showBack, showSettings = true, showMenuButton,
  onToggleMenu, onHome, onOpenSettings, onLogout
}: NavbarProps) => {
  const { isDarkMode, toggleTheme } = useTheme();
  const t = getTokens(isDarkMode);
  const s = navStyles(t);
  const config = useChurchConfig();

  // The app bar always shows the bundled product logo — it is the app's own
  // brand mark, kept fixed. The parish's uploaded logo brands the dashboard
  // banner and printed documents instead, not this bar.
  const logoSrc = churchLogo;
  const title = config.churchName || APP_TITLE;

  return (
    <nav style={s.navbar}>
      <div style={s.navLeft}>
        {showMenuButton && (
          <button
            type="button"
            className="app-nav-hamburger"
            style={s.menuBtn}
            onClick={onToggleMenu}
            aria-label="Open menu"
            title="Menu"
          >
            <Icon name="menu" size={20} />
          </button>
        )}

        <button
          type="button"
          style={{ ...s.brand, cursor: atHome ? 'default' : 'pointer' }}
          onClick={atHome ? undefined : onHome}
          title={atHome ? undefined : 'Back to Dashboard'}
        >
          <img src={logoSrc} alt="" style={s.logoImage} className="notranslate" translate="no" />
          <h2 style={s.logoText}>{title}</h2>
        </button>

        {showBack && (
          <>
            <span style={s.divider} aria-hidden="true" />
            <button style={s.backBtn} onClick={onHome}>
              <Icon name="arrowLeft" size={14} /> Back to Dashboard
            </button>
          </>
        )}
      </div>

      <div style={s.navRight}>
        <ApprovalsBell t={t} style={{ ...s.pillBtn, ...s.themeBtn }} />
        {isAdmin && showSettings && (
          <button
            style={{ ...s.pillBtn, ...s.settingsBtn }}
            onClick={onOpenSettings}
            title="Church identity, appearance, dropdown lists, users and more"
          >
            <Icon name="settings" size={14} /> Settings
          </button>
        )}

        <button style={{ ...s.pillBtn, ...s.themeBtn }} onClick={toggleTheme} title="Toggle Dark Mode">
          <Icon name={isDarkMode ? 'sun' : 'moon'} size={14} /> {isDarkMode ? 'Light' : 'Dark'}
        </button>

        <div style={s.userProfile}>
          {user?.name || 'System Admin'}
          <span style={{ opacity: 0.7, fontSize: t.fontSize.xs, marginLeft: '5px' }}>
            ({user?.userRole || 'Admin'})
          </span>
        </div>

        <button style={{ ...s.pillBtn, ...s.logoutBtn }} onClick={onLogout} title="Sign Out">
          <Icon name="logout" size={14} /> Logout
        </button>
      </div>
    </nav>
  );
};

export default Navbar;
